/**
 * HAL 9000 Eye — the iconic red eye from 2001: A Space Odyssey.
 * But this one's from Portland, so it's slightly more chill.
 *
 * Renders as an inline SVG with a glowing red lens, concentric rings,
 * and a subtle pulsing animation. Accepts a `size` prop for the
 * container dimensions (Tailwind class like "w-9 h-9").
 */
export default function HalEye({ size = 'w-9 h-9', className = '' }) {
  return (
    <div
      className={`${size} rounded-full bg-surface-900 dark:bg-black flex items-center justify-center ${className}`}
      style={{ boxShadow: '0 0 12px 2px rgba(220, 38, 38, 0.4)' }}
    >
      <svg viewBox="0 0 100 100" className="w-[75%] h-[75%]">
        <defs>
          {/* Radial gradient for the glowing lens */}
          <radialGradient id="hal-lens" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fca5a5" />
            <stop offset="25%" stopColor="#ef4444" />
            <stop offset="60%" stopColor="#dc2626" />
            <stop offset="100%" stopColor="#7f1d1d" />
          </radialGradient>
          {/* Outer glow filter */}
          <filter id="hal-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Inner highlight glow */}
          <radialGradient id="hal-highlight" cx="40%" cy="38%" r="30%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
        </defs>

        {/* Outer ring — dark metallic border */}
        <circle cx="50" cy="50" r="48" fill="none" stroke="#374151" strokeWidth="2" />
        
        {/* Middle ring — subtle dark ring */}
        <circle cx="50" cy="50" r="42" fill="none" stroke="#1f2937" strokeWidth="1.5" />

        {/* The red eye — main lens */}
        <circle cx="50" cy="50" r="38" fill="url(#hal-lens)" filter="url(#hal-glow)">
          <animate
            attributeName="opacity"
            values="0.85;1;0.85"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Inner ring detail */}
        <circle cx="50" cy="50" r="28" fill="none" stroke="rgba(127, 29, 29, 0.6)" strokeWidth="1" />
        
        {/* Core bright spot */}
        <circle cx="50" cy="50" r="14" fill="rgba(252, 165, 165, 0.3)">
          <animate
            attributeName="r"
            values="14;16;14"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Specular highlight — gives it that glassy look */}
        <circle cx="50" cy="50" r="36" fill="url(#hal-highlight)" />
      </svg>
    </div>
  )
}
